import { type CaseAreaProgressDto, type CaseDto, type CaseSummaryDto } from '@bqcrm/shared';
import { Alert, Badge, Card, Group, List, Progress, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { IconAlertTriangle, IconInbox } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api';
import { CompanyBadgeById } from '../../components/Badges';
import { Money } from '../../components/Money';
import { ProcessTaskList } from '../../components/process/ProcessTasks';
import { QueryState } from '../../components/QueryState';
import { EmptyState } from '../../components/ui';

function Area({ title, p, extra }: { title: string; p: CaseAreaProgressDto; extra?: React.ReactNode }) {
  const pct = p.max ? Math.round((p.step / p.max) * 100) : 0;
  return (
    <Stack gap={4}>
      <Group justify="space-between" gap="xs" wrap="nowrap">
        <Text size="sm" fw={600}>
          {title}
        </Text>
        <Text size="xs" c={pct === 100 ? 'green.7' : 'dimmed'} ta="right">
          {p.label}
        </Text>
      </Group>
      <Progress value={pct} color={pct === 100 ? 'green' : 'eko'} size="sm" radius="xl" />
      {extra}
    </Stack>
  );
}

/**
 * Podsumowanie sprawy (docs/10 §4): moje zadania w sprawie, postęp oferty, umowy, realizacji i rozliczeń
 * osobno dla każdej firmy, a po zakończeniu prac — co blokuje zamknięcie.
 */
export function SummaryTab({ c }: { c: CaseDto }) {
  const q = useQuery({ queryKey: ['case-summary', c.id], queryFn: () => api<CaseSummaryDto>(`/cases/${c.id}/summary`) });
  const s = q.data;
  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      {s && (
        <Stack gap="lg">
          <Stack gap="xs">
            <Title order={5}>Moje zadania w sprawie</Title>
            {s.myTasks.length ? (
              <ProcessTaskList tasks={s.myTasks} />
            ) : (
              <Card withBorder>
                <EmptyState compact icon={<IconInbox size={20} />} title="Nic nie czeka na Ciebie w tej sprawie" />
              </Card>
            )}
            {s.otherTasks.length > 0 && (
              <Text size="xs" c="dimmed">
                W toku u innych: {s.otherTasks.map((t) => `${t.title} — ${t.assigneeName ?? 'do przejęcia'}`).join(' · ')}
              </Text>
            )}
          </Stack>

          {s.blockers.length > 0 && (
            <Alert color="orange" variant="light" icon={<IconAlertTriangle size={18} />} title="Co blokuje zamknięcie sprawy">
              <List size="sm" spacing={2}>
                {s.blockers.map((b) => (
                  <List.Item key={b}>{b}</List.Item>
                ))}
              </List>
            </Alert>
          )}

          <Stack gap="xs">
            <Title order={5}>Postęp według firm</Title>
            {s.companies.length ? (
              <SimpleGrid cols={{ base: 1, md: s.companies.length > 1 ? 2 : 1 }}>
                {s.companies.map((co) => (
                  <Card key={co.companyId} withBorder padding="md">
                    <Group gap="xs" mb="sm" wrap="wrap">
                      <CompanyBadgeById id={co.companyId} />
                      <Text size="sm" c="dimmed">
                        {co.trades.join(', ')}
                      </Text>
                      {co.settlement.max > 0 && co.settlement.step === co.settlement.max && co.execution.step === co.execution.max && (
                        <Badge size="xs" color="green" variant="light">
                          komplet
                        </Badge>
                      )}
                    </Group>
                    <Stack gap="sm">
                      <Area title="Oferta" p={co.offer} />
                      <Area title="Umowa" p={co.contract} />
                      <Area title="Realizacja" p={co.execution} />
                      <Area
                        title="Rozliczenia"
                        p={co.settlement}
                        extra={
                          co.settlement.totalGr !== null && co.settlement.totalGr > 0 ? (
                            <Text size="xs" c="dimmed">
                              wpłacono <Money gr={co.settlement.paidGr ?? 0} /> z <Money gr={co.settlement.totalGr} />
                            </Text>
                          ) : null
                        }
                      />
                    </Stack>
                  </Card>
                ))}
              </SimpleGrid>
            ) : (
              <Card withBorder>
                <EmptyState compact title="Brak zakresów do realizacji" />
              </Card>
            )}
          </Stack>
        </Stack>
      )}
    </QueryState>
  );
}
